/**
 * Utils.CreditCard
 * ----------------------------------------------------------------------
 * Utilidades para formatar, validar e verificar cartões de crédito.
 *
 * @since     0.0.1
 */
export class CreditCard {
  // Propriedades Estáticas
  // --------------------------------------------------------------------

  /**
   * Dicionário contendo expressões regulares, IDs e outras informações sobre
   * cartões de crédito suportados.
   *
   * @type {Object}
   */
  static FLAG = {
    AMEX: {
      id: 3,
      name: "American Express",
      slug: "amex",
      pattern: /^3[47]\d{13}$/,
      groupPattern: /(\d{1,4})(\d{1,6})?(\d{1,5})?/
    },
    DINERS: {
      id: 4,
      name: "Diners Club",
      slug: "diners",
      pattern: /^3(?:0[0-5]|[68]\d)\d{11}$/,
      groupPattern: /(\d{1,4})?(\d{1,6})?(\d{1,4})?/
    },
    ELO: {
      id: 6,
      name: "Elo",
      slug: "elo",
      pattern: /(4011|431274|438935|451416|457393|4576|457631|457632|504175|627780|636297|636368|636369|(6503[1-3])|(6500(3[5-9]|4[0-9]|5[0-1]))|(6504(0[5-9]|1[0-9]|2[0-9]|3[0-9]))|(650(48[5-9]|49[0-9]|50[0-9]|51[1-9]|52[0-9]|53[0-7]))|(6505(4[0-9]|5[0-9]|6[0-9]|7[0-9]|8[0-9]|9[0-8]))|(6507(0[0-9]|1[0-8]))|(6507(2[0-7]))|(650(90[1-9]|91[0-9]|920))|(6516(5[2-9]|6[0-9]|7[0-9]))|(6550(0[0-9]|1[1-9]))|(6550(2[1-9]|3[0-9]|4[0-9]|5[0-8]))|(506(699|77[0-8]|7[1-6][0-9]))|(509([0-9][0-9][0-9])))/,
      groupPattern: /(\d{1,4})(\d{1,4})?(\d{1,4})?(\d{1,4})?(\d{1,3})?/
    },
    AURA: {
      id: 8,
      name: "Aura",
      slug: "aura",
      pattern: /^50\d{14}/,
      groupPattern: /(\d{1,4})(\d{1,4})?(\d{1,4})?(\d{1,4})?(\d{1,3})?/
    },
    HIPERCARD: {
      id: 5,
      name: "Hipercard",
      slug: "hipercard",
      pattern: /^(606282\d{10}(\d{3})?)|(3841\d{15})$/,
      groupPattern: /(\d{1,4})(\d{1,4})?(\d{1,4})?(\d{1,4})?(\d{1,3})?/
    },
    MASTERCARD: {
      id: 2,
      name: "Mastercard",
      slug: "master",
      pattern: /^(?:5[1-5][0-9]{2}|222[1-9]|22[3-9][0-9]|2[3-6][0-9]{2}|27[01][0-9]|2720)[0-9]{12}$/,
      groupPattern: /(\d{1,4})(\d{1,4})?(\d{1,4})?(\d{1,4})?(\d{1,3})?/
    },
    VISA: {
      id: 1,
      name: "Visa",
      slug: "visa",
      pattern: /^4\d{12}(?:\d{3})?$/,
      groupPattern: /(\d{1,4})(\d{1,4})?(\d{1,4})?(\d{1,4})?(\d{1,3})?/
    },
    DISCOVER: {
      id: 7,
      name: "Discover",
      slug: "discover",
      pattern: /^(6011\d{12,15}|622\d{13,16}|64\d{14,17}|65\d{14,17})/,
      groupPattern: /(\d{1,4})(\d{1,4})?(\d{1,4})?(\d{1,4})?(\d{1,3})?/
    },
    JCB: {
      id: 9,
      name: "JCB",
      slug: "jcb",
      pattern: /^35\d{14,17}/,
      groupPattern: /(\d{1,4})(\d{1,4})?(\d{1,4})?(\d{1,4})?(\d{1,3})?/
    }
  };

  // Métodos Estáticos
  // --------------------------------------------------------------------

  /**
   * Usado para evitar calcular números na soma, contém o resultado da
   * operação `n * 2` e `(n * 2) - 9` (para números cuja multiplicação resulte
   * em algo maior que 9).
   *
   * @type {Array}
   */
  static MODULO = [0, 2, 4, 6, 8, 1, 3, 5, 7, 9];

  /**
   * Utiliza o padrão de mascaramento da flag para formatar o número do cartão.
   *
   * @param {String|Number} input
   *     Número do cartão para teste
   * @returns {String}
   */
  static formatCard = (input) => {
    input = CreditCard.clean(input);

    let flag = CreditCard.getCardFlag(input),
      frag = [],
      matches;

    if (false === flag) return input;

    matches = flag.groupPattern.exec(input);

    for (let n = 1; n < matches.length; n++) {
      if (matches[n] !== undefined) frag.push(matches[n]);
    }

    return frag.join(" ");
  };

  /**
   * Utiliza o padrão de mascaramento da flag para retornar o número do cartão
   * com uma máscara de caracteres para exibição no front.
   *
   * @param {String|Number} input
   *     Número do cartão para teste
   * @returns {String}
   */
  static maskCard = (input) => {
    input = CreditCard.clean(input);

    let flag = CreditCard.getCardFlag(input),
      frag = [],
      matches;

    if (false === flag) {
      let chars = input.split("");
      for (let n = 0; n < chars.length - 4; n++) chars[n] = "*";
      return chars.join("");
    }

    matches = input.match(flag.groupPattern);

    for (let n = 1; n < matches.length; n++) {
      if (matches[n] !== undefined) frag.push(matches[n]);
    }

    for (let i = 0; i < frag.length; i++) {
      if (i < (frag.length - 1)) {
        frag[i] = frag[i].replace(/\d/g, "*");
      }
    }

    return frag.join(" ");
  };

  /**
   * Retorna o objeto com dados da bandeira do cartão ou boolean `false` se
   * inválido.
   *
   * @param {String|Number} input
   *     Cartão de crédito para verificação
   * @returns {Object|Boolean}
   */
  static getCardFlag = (input) => {
    input = CreditCard.clean(input);
    let keys = Object.keys(CreditCard.FLAG);
    for (let n of keys) {
      let flag = CreditCard.FLAG[n];
      if (flag.pattern.test(input) === true) return flag;
    }
    return false;
  };

  /**
   * Valida a bandeira do cartão usando as flags no dicionário, caso nenhuma
   * seja válida, retorna "none".
   *
   * @returns {String}
   */
  static validateFlag = (input) => {
    let flag = CreditCard.getCardFlag(input);
    if (input !== false) return flag.slug;
    return "none";
  };

  /**
   * Valida o dígito de um cartão de crédito usando algoritmo de Luhn.
   *
   * @param {String|*} input
   *     Número de cartão de crédito a ser validado
   * @returns {Boolean}
   */
  static validateDigit = (input) => {
    input = CreditCard.clean(input);
    input = input.trim().split("");

    let inputDg = input,
      sumDg = 0,
      digit = inputDg.pop(),
      check;

    inputDg = inputDg.reverse();

    for (let n = 0; n < inputDg.length; n++) {
      let _val = parseInt(inputDg[n]);
      sumDg += (n % 2 === 0) ? CreditCard.MODULO[_val] : _val;
    }

    // Set verifier digit
    check = (10 - (sumDg % 10)) % 10;

    return (check === parseInt(digit));
  };

  /**
   * Valida o modulo do número usando algoritmo de Luhn.
   *
   * No caso, para que o número seja válido, o `mod10` deve ser 0.
   *
   * @param {String|*} input
   *     Número de cartão de crédito a ser validado
   * @returns {Boolean}
   */
  static validateModulo = (input) => {
    let sumMd = 0;

    input = CreditCard.clean(input);
    input = input.trim().split("");
    input = input.reverse();

    for (let n = 0; n < input.length; n++) {
      let _val = parseInt(input[n]);
      sumMd += (n % 2 === 0) ? _val : CreditCard.MODULO[_val];
    }

    return (sumMd % 10 === 0);
  };

  /**
   * Remove caracteres indesejados do input.
   *
   * @param {String|Number} input
   *     String/número para limpeza
   * @returns {String}
   */
  static clean = (input) => {
    if (typeof input === "number") input = input.toString();
    if (input && input.length > 0) input = input.replace(/\D/g, "");
    return input;
  };
}
